<template>
  <PageWrapper>
    <NavbarMain />
    <div class="mt-5 reports__container">
      <div class="profile__info__container">
        <header class="is-flex">
          <b-icon
            size="is-medium"
            icon="chart-box-outline"
            class="mr-2"
          ></b-icon>
          <h1>Reports</h1>
        </header>
        <div>
          <div v-if="loading" class="loading__container mt-4">
            <b-skeleton
              height="400px"
              width="270px"
              :active="true"
            ></b-skeleton>
            <div class="mt-4">
              <b-skeleton
                height="50px"
                width="270px"
                :active="true"
              ></b-skeleton>
            </div>
            <div class="is-flex mt-4" style="gap: 1rem">
              <b-skeleton
                height="100px"
                width="128px"
                :active="true"
              ></b-skeleton>
              <b-skeleton
                height="100px"
                width="128px"
                :active="true"
              ></b-skeleton>
            </div>
          </div>
          <div v-else class="profile__card">
            <picture class="profile__picture">
              <img :src="userPhoto" alt="user profile photo" />
              <div class="profile__picture__content">
                <h2>{{ user.name }}</h2>
                <p class="profile__email">{{ user.email }}</p>
              </div>
            </picture>

            <div class="profile__content">
              <div v-if="isProUser" class="prouser__sign__container">
                <ProUserSign />
              </div>
              <p class="profile__date">Member since {{ createdAt }}</p>
            </div>

            <div class="profile__content__quotes">
              <div>
                <p>{{ userSummary.quotes }}</p>
                <span>Requests</span>
              </div>
              <div>
                <p>{{ userSummary.confirmed }}</p>
                <span>Won</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div class="profile__reports__container">
        <b-tabs>
          <b-tab-item label="Requests">
            <RequestReport />
          </b-tab-item>
          <b-tab-item label="Payments">
            <PaymentReport />
          </b-tab-item>
          <b-tab-item label="Quotes">
            <QuoteReport />
          </b-tab-item>
        </b-tabs>
      </div>
    </div>
  </PageWrapper>
</template>

<script>
import { mapGetters } from "vuex";
import helpers from "@/helpers";
import NavbarMain from "@/components/_shared/NavbarMain.vue";
import PageWrapper from "@/components/_shared/PageWrapper.vue";
import ProUserSign from "../components/_shared/ProUserSign.vue";
import { USER_ROLE } from "@/constants";
import { PATH_INFO } from "@/router/path";
import RequestReport from "@/components/reports/RequestReport.vue";
import PaymentReport from "@/components/reports/PaymentReport.vue";
import QuoteReport from "@/components/reports/QuoteReport.vue";

export default {
  name: "Reports",
  components: {
    QuoteReport,
    PaymentReport,
    RequestReport,
    PageWrapper,
    NavbarMain,
    ProUserSign,
  },
  data() {
    return {
      loading: false,
      form: {
        data: null,
        assets_name: "",
      },
      image: null,
      preview: null,
      dateToStringOptions: {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      },
    };
  },
  computed: {
    ...mapGetters({
      authData: "auth/getAuthData",
      requestsByTime: "reports/getQuotesByTime",
      requestsByClients: "reports/getQuotesByClients",
      requestsByStage: "reports/getRequestsByStages",
      requestsByDestinations: "reports/getRequestsByDestinations",
      salesReport: "reports/getSalesReport",
      userSummary: "reports/getUserSummary",
      clients: "client/getClients",
      authUser: "auth/getAuthUserData",
      users: "users/getUsers",
    }),
    user() {
      return this.authData.user;
    },
    isProUser() {
      return this.authData.user_type.name === "pro";
    },
    createdAt() {
      return new Date(this.user.created_at).toLocaleDateString(
        undefined,
        this.dateToStringOptions
      );
    },
    userPhoto() {
      if (this.preview) return this.preview;
      if (this.form.assets_name) return this.form.data;
      return (
        this.user.photo ??
        "https://place-hold.it/610x380/DDDDDD/fff.jpg?bold&fontsize=50&text=UserPhoto"
      );
    },
  },
  created() {
    const canViewReportsSection = !helpers.hasAnyRoles(
      this.authData.user,
      [USER_ROLE.TRAVEL_AGENT],
      false
    );

    if (!canViewReportsSection) {
      this.$router.push({
        path: PATH_INFO.HOME.path,
      });
    }
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    async loadUserSummary() {
      await this.$store.dispatch("reports/loadUserSummary");
    },
    async loadInitialData() {
      this.loading = true;
      await this.loadUserSummary();
      this.loading = false;
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
h1 {
  font-weight: 700;
  font-size: 1.5rem;
}
.bg-transparent {
  background-color: transparent;
  color: #fff;
}
.loading__container {
  display: flex;
  flex-direction: column;
}
.loading__container .b-skeleton + .b-skeleton {
  margin-top: 0rem;
}
.profile__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 0.5rem;
  margin-top: 2rem;
}
.profile__picture {
  width: 8rem;
  height: 8rem;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 1rem;
  height: 18rem;
  width: 100%;
  border-radius: 1rem;
  position: relative;
}
.profile__picture img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.profile__picture__content {
  position: absolute;
  bottom: 0;
  left: 0;
  color: #eee;
  width: 100%;
  padding: 0.5rem;
}
.profile__picture__content > * {
  width: 100%;
  text-align: center;
}
.profile__picture__content h2 {
  font-size: 1rem;
  font-weight: 500;
}
.profile__picture__content .profile__email {
  font-size: 0.9rem;
}
.profile__content {
  margin: 1rem 0;
  text-align: center;
}
.profile__content h2 {
  font-weight: 700;
  font-size: 1.2rem;
}
.profile__date {
  color: #bebebe;
  margin: 0.5rem 0;
  margin-top: 1.5rem;
}
#file {
  display: none;
}
.prouser__sign__container {
  position: relative;
  margin: 0 auto;
  text-align: center;
  display: flex;
  justify-content: center;
}
.profile__content__quotes {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}
.profile__content__quotes > div {
  display: grid;
  place-items: center;
  padding: 1rem;
  box-shadow: 0px 0px 30px -12px #ccc;
  flex: 1;
  border-radius: 0.5rem;
}
.profile__content__quotes > div p {
  font-weight: 600;
}
.profile__content__quotes > div:last-of-type {
  background-color: #8ed793;
}
.reports__container {
  display: flex;
  gap: 2rem;
}
.reports__container hr {
  height: 100%;
  width: 2px;
  background-color: #ccc;
}
.profile__info__container {
  min-width: 14rem;
}
.profile__reports__container {
  width: 100%;
}
.profile__reports__container h2 {
  font-weight: 800;
  font-size: 1.25rem;
}
.reports__charts__container {
  flex-grow: 1;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.chart__container {
  width: 25rem;
  box-shadow: 0px 0px 30px -15px #ccc;
  padding: 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  /* justify-content: center; */
  align-items: center;
  position: relative;
  gap: 1rem;
}
.chart__container.selected {
  width: 100%;
  /* height: 100%; */
  justify-content: flex-start;
}
.chart__container:hover {
  border: 1px solid #ccc;
  transition: all 0.3s linear;
}

.chart__header__options {
  gap: 0.5rem;
  display: none;
}
.chart__container.selected .chart__header__options {
  display: flex;
}
.chart__header__options > button {
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  font-size: 0.85rem;
}
.source_data_modal__container {
  padding: 1rem;
}
.source_data_table th,
.source_data_table td {
  text-align: center !important;
  vertical-align: middle !important;
}
.source_data_table.b-table .table th .th-wrap {
  justify-content: center;
}

.edit_photo__button,
.edit_photo__button span {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.edit_photo__button .icon {
  margin-left: 0rem !important;
  margin-right: 0rem !important;
}

.report_config__select {
  /* width: 20rem; */
  width: 50%;
}

.range-date label,
.report_config__select label {
  font-size: 0.8rem;
}

.request_date__field {
  height: 1.75rem;
  min-width: 49%;
}

/* .profile__reports__container .multiselect {
  min-width: 50%;
  max-width: 50%;
} */

.sales_report__container {
  box-shadow: 0px 0px 25px -10px #ccc;
  padding: 1.4rem;
  border-radius: 0.5rem;
}

@media screen and (max-width: 480px) {
  .reports__container {
    flex-direction: column;
    height: 100%;
  }
  .reports__container hr {
    width: 100%;
    height: 2px;
    margin: 0.25rem 0;
  }
  .reports__charts__container {
    margin-top: 2rem;
  }
  .chart__container {
    pointer-events: none;
  }
}
</style>
